/**
 * FolderCreateModal Component
 * 
 * Modal for creating or editing folders
 */

import React, { useState, useEffect } from 'react';
import { DocumentFolder } from '../types/documentsTypes';
import { X, Folder, Palette } from 'lucide-react';
import { cn } from '@/core/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface FolderCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFolderData) => void;
  folder?: DocumentFolder | null; // For editing
  parentFolder?: DocumentFolder | null;
  isLoading?: boolean;
}

export interface CreateFolderData {
  name: string;
  description: string;
  color: string;
  parent_id: string | null;
}

// ============================================================================
// Constants
// ============================================================================

const FOLDER_COLORS = [
  { name: 'Default', value: 'gray', class: 'bg-gray-400' },
  { name: 'Sage', value: 'sage', class: 'bg-sage' },
  { name: 'Blue', value: 'blue', class: 'bg-blue-500' },
  { name: 'Purple', value: 'purple', class: 'bg-purple-500' },
  { name: 'Orange', value: 'orange', class: 'bg-orange-500' },
  { name: 'Red', value: 'red', class: 'bg-red-500' },
  { name: 'Yellow', value: 'yellow', class: 'bg-yellow-500' },
  { name: 'Pink', value: 'pink', class: 'bg-pink-500' },
];

// ============================================================================
// Component
// ============================================================================

export const FolderCreateModal: React.FC<FolderCreateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  folder,
  parentFolder,
  isLoading = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('gray');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!folder;

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (folder) {
        setName(folder.name);
        setDescription(folder.description || '');
        setColor(folder.color || 'gray');
      } else {
        setName('');
        setDescription('');
        setColor('gray');
      }
      setErrors({});
    }
  }, [isOpen, folder]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Folder name is required';
    } else if (name.length > 200) {
      newErrors.name = 'Name must be 200 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    onSubmit({
      name: name.trim(),
      description: description.trim(),
      color,
      parent_id: folder?.parent_id ?? parentFolder?.id ?? null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-navy/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
              <Folder className="w-5 h-5 text-sage" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-navy">
                {isEditing ? 'Edit Folder' : 'New Folder'}
              </h2>
              {parentFolder && !isEditing && (
                <p className="text-sm text-warm-gray">
                  Inside: {parentFolder.name}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-cream transition-colors"
          >
            <X className="w-5 h-5 text-warm-gray" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">
              Folder Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Medical Records 2024"
              className={cn(
                'w-full px-4 py-2.5 rounded-lg border transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-sage/20 focus:border-sage',
                errors.name 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-200 bg-white'
              )}
              autoFocus
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className={cn(
                'w-full px-4 py-2.5 rounded-lg border border-gray-200 transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-sage/20 focus:border-sage',
                'resize-none'
              )}
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              <span className="flex items-center gap-1.5">
                <Palette className="w-4 h-4" />
                Folder Color
              </span>
            </label>
            <div className="flex flex-wrap gap-2">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  className={cn(
                    'w-8 h-8 rounded-full transition-all',
                    c.class,
                    color === c.value 
                      ? 'ring-2 ring-offset-2 ring-navy scale-110' 
                      : 'hover:scale-105'
                  )}
                  title={c.name}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-warm-gray hover:text-navy transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                'px-6 py-2 rounded-lg font-medium transition-all',
                'bg-sage text-white hover:bg-sage-dark',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FolderCreateModal;

